import {
  HandHistoryEvent,
  HandHistorySnapshot,
  PotGroupPlayerSnapshot
} from 'components/HandHistory/types';

export const PREVIEW_PLAYER_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9];
/** What the playground opens with: a full-enough table to judge reveal and muck rows. */
export const DEFAULT_PREVIEW_PLAYER_COUNT = 6;

// Extra participants fold without contributing, preserving the scenario's
// cards, awards and net results. Never mutate the shared fixture objects.
export const withPreviewPlayerCount = (
  hand: HandHistorySnapshot,
  requestedCount: number | undefined
): HandHistorySnapshot => {
  const originalCount = hand.tableSnapshot.seats.length;
  const count = Math.max(originalCount, Math.min(9, Math.floor(requestedCount ?? originalCount)));
  if (count === originalCount) return hand;

  const extras: PotGroupPlayerSnapshot[] = Array.from(
    { length: count - originalCount },
    (_, index) => {
      const seat = originalCount + index + 1;
      return {
        playerId: `preview-extra-${seat}`,
        seat,
        name: `Player ${seat}`,
        folded: true,
        badge: 'FOLD',
        cards: [],
        cardsRevealed: false,
        grossWin: 0,
        net: 0
      };
    }
  );
  // Heads-up's dealer is also the small blind. At a larger table, place the
  // dealer immediately before that seat so the existing blinds stay valid.
  const dealerSeat = originalCount === 2 ? count : hand.tableSnapshot.dealerSeat;
  const withDealer = <T extends { seat: number }>(entry: T) => ({
    ...entry,
    isDealer: entry.seat === dealerSeat
  });
  const smallBlindName = hand.tableSnapshot.seats[0].name;
  let blindCount = 0;
  const playByPlay = hand.playByPlay.flatMap((event, index): HandHistoryEvent[] => {
    const events = [event];
    if (event.type === 'blind' && ++blindCount === 2) {
      events.push(...extras.map((p) => ({ ...event, type: 'action', text: `${p.name} folds` })));
    }
    // The small blind acts first after the flop once the table is no longer
    // heads-up. Check to the original big-blind bettor, then retain the action.
    if (
      originalCount === 2 &&
      event.tableCards &&
      / bets /.test(hand.playByPlay[index + 1]?.text ?? '')
    ) {
      events.push({
        ...event,
        type: 'action',
        tableCards: undefined,
        text: `${smallBlindName} checks`
      });
    }
    return events;
  });
  return {
    ...hand,
    tableSnapshot: {
      ...hand.tableSnapshot,
      totalSeats: count,
      dealerSeat,
      seats: [
        ...hand.tableSnapshot.seats,
        ...extras.map((p) => ({ ...p, stack: hand.gameType === 'cash' ? 100 : 5000 }))
      ].map(withDealer)
    },
    pots: hand.pots?.map((pot) => ({
      ...pot,
      awards: pot.awards.map((award) => ({
        ...award,
        players: award.players.map(withDealer)
      }))
    })),
    potGroups: [
      ...hand.potGroups
        .filter((pot) => pot.potType !== 'no_payout')
        .map((pot) => ({
          ...pot,
          players: pot.players.map(withDealer)
        })),
      {
        name: 'No Payout',
        potType: 'no_payout',
        amount: 0,
        players: [
          ...hand.potGroups
            .filter((pot) => pot.potType === 'no_payout')
            .flatMap((pot) => pot.players),
          ...extras
        ].map(withDealer)
      }
    ],
    playByPlay: playByPlay.map((event, index) => ({
      ...event,
      id: `${hand.handId}-players-${count}-${index}`,
      order: index + 1,
      ts: hand.completedAt - (playByPlay.length - index) * 2000
    }))
  };
};
